import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Building2, ChevronDown, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import type { ClassificationType, EntityData } from "@/types/entity";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { cn } from "@/lib/utils";
import { isSessionEmpty, type SessionWithEntity } from "@/utils/multi-session";
import { getSessionActions } from "@/utils/sessionActions";

type SessionSwitcherProps = {
  entityData?: EntityData | null;
};

export function SessionSwitcher({ entityData }: SessionSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const {
    sessions,
    isLoadingSessions,
    currentSessionId,
    isLoadingSessionSwitch,
    createSession,
    switchToSession,
    archiveSessionById,
    deleteSessionById,
    getSessionDisplayName,
  } = useSessionSwitcher();

  const handleCreateSession = async () => {
    const success = await createSession();
    if (success) {
      navigate("/dashboard");
    }
  };

  const hasBlankSession = sessions.some(isSessionEmpty);

  if (isLoadingSessions) {
    return (
      <div className="p-3 bg-navy-950 rounded-xl">
        <div className="flex items-center gap-3">
          <Skeleton className="w-10 h-10 rounded-lg bg-navy-900" />
          <div className="flex-1">
            <Skeleton className="h-3 w-20 mb-2 bg-navy-900" />
            <Skeleton className="h-4 w-32 bg-navy-900" />
          </div>
        </div>
        <Skeleton className="h-5 w-28 mt-3 bg-navy-900" />
      </div>
    );
  }

  const classification = entityData
    ? (entityData.nis_classification?.classificationV2 as
        | ClassificationType
        | undefined)
    : null;

  return (
    <div className="bg-navy-950 rounded-xl overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Entity Header - Clickable to expand */}
        <CollapsibleTrigger asChild>
          <button
            className="w-full p-3 text-left"
            disabled={isLoadingSessionSwitch}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-navy-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 size={20} className="text-ds-blue-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-ds-blue-300">Entitate activă</p>
                <p className="text-sm font-semibold text-white">
                  {entityData?.nume_entitate || "-"}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <ClassificationBadge
                classification={classification}
                variant="dark"
                size="sm"
              />
              <ChevronDown
                size={16}
                className={cn(
                  "text-ds-blue-300 transition-transform",
                  isOpen && "rotate-180"
                )}
              />
            </div>
          </button>
        </CollapsibleTrigger>

        {/* Expanded Content - Sessions List */}
        <CollapsibleContent>
          <div className="border-t border-navy-800 p-3 space-y-3">
            {/* Sessions List - Filter out the active session (already shown in header) */}
            <div className="space-y-2">
              {sessions
                .filter((session) => session.id !== currentSessionId)
                .map((session, index) => (
                  <SessionItemDark
                    key={session.id}
                    session={session}
                    index={index}
                    isActive={false}
                    isLoading={isLoadingSessionSwitch}
                    onSwitch={async () => {
                      await switchToSession(session.id);
                      navigate("/dashboard");
                    }}
                    onArchive={() => archiveSessionById(session.id)}
                    onDelete={() => deleteSessionById(session.id)}
                    getDisplayName={getSessionDisplayName}
                  />
                ))}

              {sessions.filter((s) => s.id !== currentSessionId).length ===
                0 && (
                <div className="text-center py-4 text-sm text-ds-blue-300">
                  Nu există companii
                </div>
              )}
            </div>

            {/* Create New Session Button */}
            {(() => {
              const createButton = (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCreateSession}
                  disabled={isLoadingSessionSwitch || hasBlankSession}
                  className={cn(
                    "text-xs w-full justify-center gap-2 h-9",
                    "bg-navy-700 border border-ds-blue-500/40 text-ds-blue-200 ring-1 ring-inset ring-white/[0.06]",
                    "hover:bg-navy-600 hover:border-ds-blue-500/50 hover:ring-ds-blue-500/30",
                    "[&_svg]:text-ds-blue-400",
                    "disabled:border-navy-800 disabled:bg-navy-900/40 disabled:text-ds-text-400 disabled:ring-0",
                    "disabled:[&_svg]:text-ds-text-400 disabled:hover:bg-navy-900/40"
                  )}
                >
                  <Plus className="h-4 w-4" />
                  Companie nouă
                </Button>
              );

              return hasBlankSession ? (
                <TouchTooltip
                  content={
                    <p className="text-xs">Nu poți adăuga o companie nouă</p>
                  }
                >
                  <div className="w-full">{createButton}</div>
                </TouchTooltip>
              ) : (
                createButton
              );
            })()}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

type SessionItemDarkProps = {
  session: SessionWithEntity;
  index: number;
  isActive: boolean;
  isLoading: boolean;
  onSwitch: () => void;
  onArchive: () => void;
  onDelete: () => void;
  getDisplayName: (session: SessionWithEntity, index: number) => string;
};

function SessionItemDark({
  session,
  index,
  isActive,
  isLoading,
  onSwitch,
  onArchive,
  onDelete,
  getDisplayName,
}: SessionItemDarkProps) {
  const displayName = getDisplayName(session, index);
  const { canArchive } = getSessionActions(session);

  return (
    <div
      className={cn(
        "relative group rounded-lg border md:p-3 p-2 transition-all",
        isActive
          ? "border-ds-emerald-500 bg-ds-emerald-600 text-white"
          : "bg-navy-700 border-navy-700 ring-1 ring-inset ring-white/[0.06] hover:bg-navy-600 hover:border-ds-blue-500/50 hover:ring-ds-blue-500/30"
      )}
    >
      {/* Session Content - Clickable area */}
      <button
        onClick={onSwitch}
        disabled={isActive || isLoading}
        className="w-full text-left space-y-2"
      >
        {/* Header with title only */}
        <div className="flex items-start justify-between gap-2">
          <div className={cn("flex-1 min-w-0", !isActive && "pr-8")}>
            <div
              className={cn(
                "text-xs md:text-sm font-medium",
                isActive ? "text-white" : "text-ds-blue-100"
              )}
            >
              {displayName}
            </div>
          </div>
        </div>
      </button>

      {/* Empty sessions get a hard-delete shortcut (no dossier to preserve).
          Non-empty sessions can only be archived here; hard-delete for those
          lives behind the type-to-confirm dialog on /companiile-mele. */}
      {!isActive && !canArchive && (
        <TouchTooltip
          content={<p className="text-xs">Șterge compania goală</p>}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute top-2 right-2 h-6 w-6 p-0 text-ds-blue-300 hover:bg-ds-red-500/20 hover:text-ds-red-200"
            disabled={isLoading}
            aria-label={`Șterge compania goală ${displayName}`}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </TouchTooltip>
      )}
      {!isActive && canArchive && (
        <TouchTooltip content={<p className="text-xs">Arhivează compania</p>}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onArchive();
            }}
            className="absolute top-2 right-2 h-6 w-6 p-0 text-ds-blue-300 hover:bg-ds-blue-500/20 hover:text-ds-blue-200"
            disabled={isLoading}
            aria-label={`Arhivează compania ${displayName}`}
          >
            <Archive className="h-3 w-3" />
          </Button>
        </TouchTooltip>
      )}

      {/* Loading overlay */}
      {isLoading && isActive && (
        <div className="absolute inset-0 bg-navy-950/50 rounded-lg flex items-center justify-center">
          <div className="text-xs text-ds-blue-100">Se încarcă...</div>
        </div>
      )}
    </div>
  );
}
